import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { Control, SubmitHandler, useForm } from 'react-hook-form'
import AccountSection from '../AccountSection/AccountSection'
import Button from '../Button/Button'
import { PasswordInput, PasswordInputStandalone } from '../Form'
import Notification from '../Notification/Notification'

export interface PasswordData {
  currentPassword: string
  newPassword: string
  newPasswordConfirmation: string
}

export interface AccountChangePasswordProps {
  error?: Error | null
  /**
   * Callback function when the form is submitted.
   */
  onSubmit: SubmitHandler<PasswordData>
}

export default function AccountChangePassword({
  error = null,
  onSubmit,
}: Readonly<AccountChangePasswordProps>) {
  const [isEdit, setIsEdit] = useState(false)

  const { control, handleSubmit, reset, watch, trigger } =
    useForm<PasswordData>()

  const password = watch('newPassword')
  const confirmPassword = watch('newPasswordConfirmation')

  const handleChangePassword = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      setIsEdit(true)
    },
    [],
  )

  const handleCancel = useCallback(() => {
    setIsEdit(false)
    reset()
  }, [reset])

  const submitForm = useCallback(
    (data: PasswordData, event: React.BaseSyntheticEvent) =>
      (onSubmit(data) as Promise<boolean>).then((success: boolean) =>
        success ? setIsEdit(false) : event.preventDefault(),
      ),
    [onSubmit],
  )

  useEffect(() => {
    // Trigger validation of "confirm password" when the "password" value changes
    if (confirmPassword) trigger('newPasswordConfirmation')
  }, [password, confirmPassword, trigger])

  return (
    <AccountSection
      title="Change password"
      link={
        !isEdit
          ? {
              title: 'Change password',
              onClick: handleChangePassword,
              type: 'button',
            }
          : undefined
      }
    >
      <form
        onSubmit={(event) =>
          handleSubmit((data) => submitForm(data, event))(event)
        }
        className="flex flex-col gap-y-4 md:gap-y-6"
      >
        {!isEdit ? (
          <PasswordInputStandalone
            name="currentPassword"
            label="Current Password"
            defaultValue="************"
            className="[&_button]:hidden"
            disabled={true}
          />
        ) : (
          <>
            {error?.message && (
              <Notification title={error?.message} type={'urgent'} />
            )}
            <PasswordInput
              name="currentPassword"
              label="Current Password"
              control={control as unknown as Control}
              className="[&_input]:mb-0"
              rules={{
                minLength: { value: 6, message: 'Password is too short' },
                required: { value: true, message: 'Password is required' },
              }}
            />
            <PasswordInput
              name="newPassword"
              label="New password"
              control={control as unknown as Control}
              className="[&_input]:mb-0"
              rules={{
                minLength: { value: 6, message: 'Password is too short' },
                required: { value: true, message: 'Password is required' },
              }}
            />
            <PasswordInput
              name="newPasswordConfirmation"
              label="Password confirmation"
              control={control as unknown as Control}
              className="[&_input]:mb-0"
              rules={{
                minLength: { value: 6, message: 'Password is too short' },
                required: { value: true, message: 'Password is required' },
                validate: (val: string) => {
                  if (watch('newPassword') !== val) {
                    return 'Please confirm passwords match'
                  }
                },
              }}
            />
            <div className="flex items-center gap-x-4">
              <Button type="submit" className="w-[120px]">
                Save
              </Button>
              <Button
                onClick={handleCancel}
                className="w-[120px]"
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </form>
    </AccountSection>
  )
}
